/** advanced.js - constants related to creating advanced scripts. */

import { SeasonName } from 'store/types/types';
import { SUPPORTED_COUNTRIES } from './countryCodes';
import { cropIds } from './crops';
import { UNIT_TYPES } from './unitTypes';

// Expresses how a field meets the requirements for creating a new advanced script.
export const advancedScriptRequirements = [
  'MEETS_WITH_YIELD_ONLY', // Meets requirements using only yield data. CCI data may or may not be available.
  'MEETS_WITH_CCI', // Field meets requirements with CCI data
  'DOES_NOT_MEET', // More data will be needed for the field--not currently possible to create a new rx.
  'PENDING' // Have not yet gotten layer data to evaluate
].reduce((result, key) => { result[key] = `${key.toLowerCase()}`; return result; }, {});

export const layerTypes = {
  YIELD: 'yield',
  CCI: 'cci'
} as const;

export const requiredNumImages = {
  [layerTypes.YIELD]: 2,
  [layerTypes.CCI]: 3
};

// In case where user must enter their harvest history correlating to available CCI layer seasons,
// this is the number of seasons that must match a supported crop, e.g. corn.
export const requiredNumSupportedCropImages = {
  [layerTypes.YIELD]: 1,
  [layerTypes.CCI]: 1
};

export const supportedCropIds = [
  cropIds.CORN
];

// As sent by hank
export const ADVANCED_SCRIPTING_AVAILABILITY_SUCCESS = 'Success';

export const AGRONOMIC_SEASONS = {
  SAF: 'SAF',
  SPR: 'SPR',
  SUM: 'SUM'
};

export const AGRONOMIC_SEASONS_BY_COUNTRY = {
  [SUPPORTED_COUNTRIES.BRAZIL]: [
    AGRONOMIC_SEASONS.SAF,
    AGRONOMIC_SEASONS.SUM
  ]
};

export const SEASONS_NAMES_BY_CODE = {
  SAF: SeasonName.SAFRINHA,
  SPR: SeasonName.SPRING,
  SUM: SeasonName.SUMMER,
  FAL: SeasonName.FALL,
  WIN: SeasonName.WINTER,
};

export const MULTIPLE_AGRONOMIC_SEASONS_COUNTRIES = [
  SUPPORTED_COUNTRIES.BRAZIL
];

export const LINE_TYPES = {
  FIELD_AVERAGE_YIELD: 'fieldAverageYield',
  BY_ZONES_YIELD: 'byZonesYield',
};

/**
 * Ratios used to calculate the minimum for validations from the maximums
 * which come from web-assets scripting validations. The values are not changed there because
 * that would affect the validation over the zone application (validateZoneApplicationProperty function).
 */
export const RATIOS = {
  MIN_RATE: 0.2,
  MIN_TARGET_YIELD: 0.1,
  MIN_GRAIN_PRICE: 0.006,
  RECOMMENDATION_GENERATION_MIN_RATE: 1/3
};

// Minimum value accepted by the model
export const VALID_RECOMMENDATION_GENERATION_MIN_RATE = 20000;
export const MIN_TARGET_YIELD = 1;

// Number of ticks for the X axis in the curves graph
export const X_AXIS_TICKS = 5;
export const X_AXIS_STEPS = {
  [UNIT_TYPES.seed_ac]: 5_000,
  [UNIT_TYPES.seed_ha]: 10_000,
};

/**
 * These percentages should be used as a base to
 * expand the curves to the right and the left
 */
export const CURVES_EXPANSION_RATE = {
  LOWER: 0.8,
  UPPER: 0.5
};

export const HANK_POLLING_TIMEOUT = 600;
